import { TelemetryEvent, TelemetryMeasurement, trackError, trackEvent, trackMeasurement } from './telemetry';
import { getDeviceContext } from './deviceContext';

/**
 * Bridges the offline package's queue telemetry into our Grafana vocabulary.
 *
 * The queue itself now lives in `@4r/common-module-offline-services`, mounted by
 * `app-mf-work-order` — a different bundle. That MF cannot import this module
 * (its own copy would hold a `Faro` instance of `undefined` and report nothing),
 * so it forwards the raw event through the host service registry and the naming
 * happens here, next to the enum the dashboard queries are written against.
 *
 * The shape below mirrors `OfflineTelemetryEvent` from the package. It is
 * duplicated rather than imported because the host deliberately carries no
 * dependency on the offline package — keep the two in step by hand.
 */

export type OfflineQueueName = 'main' | 'file';

interface CommandFields {
	queue: OfflineQueueName;
	commandType: string;
	commandId: string;
	orderId?: string;
	attempts?: number;
	queuedForMs?: number;
	lastError?: string;
}

export type OfflineQueueTelemetryEvent =
	| ({
			type:
				| 'command:enqueued'
				| 'command:syncing'
				| 'command:succeeded'
				| 'command:requeued'
				| 'command:stalled'
				| 'command:failed'
				/** Sent again or dropped from the sync status view (offline-services 1.5.8+). */
				| 'command:retried'
				| 'command:discarded';
	  } & CommandFields)
	| { type: 'queue:auth-required'; queue: OfflineQueueName }
	/** The legacy host queue was moved into the package's queue (offline-services 1.5.4+). */
	| { type: 'queue:legacy-import'; imported: number; dropped: number }
	/** Start-up housekeeping removed something (offline-services 1.5.10+). */
	| { type: 'store:retention'; evictedOrders: number; strippedUploads: number }
	| { type: 'queue:tick'; queue: OfflineQueueName; durationMs: number; processed: boolean }
	/**
	 * Sampled by the MF rather than emitted by the queue — producing it inside the
	 * package would mean reading every queued row on each tick, while the MF's
	 * `useSyncQueue` already holds the list.
	 */
	| { type: 'queue:backlog'; depth: number; oldestAgeMs: number };

/** `storeName` is what the legacy dashboards grouped by; keep the same key. */
const storeNameOf = (queue: OfflineQueueName) => (queue === 'file' ? 'FileCommands' : 'Commands');

export function reportOfflineQueueEvent(event: OfflineQueueTelemetryEvent): void {
	if (event.type === 'queue:auth-required') {
		trackEvent(TelemetryEvent.OfflineCommandExhausted, {
			storeName: storeNameOf(event.queue),
			reason: '401',
		});
		return;
	}

	if (event.type === 'queue:legacy-import') {
		// How much work technicians carried across the upgrade — and how much of it
		// could not be sent (retired command types, uploads whose bytes were gone).
		trackEvent(TelemetryEvent.OfflineLegacyQueueImported, {
			imported: event.imported,
			dropped: event.dropped,
		});
		if (event.dropped > 0) {
			trackEvent(TelemetryEvent.OfflineCommandDiscarded, {
				reason: 'legacy-import',
				count: event.dropped,
			});
		}
		return;
	}

	if (event.type === 'store:retention') {
		trackEvent(TelemetryEvent.OfflineStoreRetention, {
			evictedOrders: event.evictedOrders,
			strippedUploads: event.strippedUploads,
		});
		if (event.strippedUploads > 0) {
			// Photos that never reached the backend and are now gone from the device.
			trackEvent(TelemetryEvent.OfflineCommandDiscarded, {
				reason: 'retention',
				count: event.strippedUploads,
			});
		}
		return;
	}

	if (event.type === 'queue:backlog') {
		trackMeasurement(TelemetryMeasurement.OfflineQueue, {
			depth: event.depth,
			oldestAgeMs: event.oldestAgeMs,
		});
		return;
	}

	if (event.type === 'queue:tick') {
		// Only a pass that actually did work is worth a data point; an idle tick
		// every 10 seconds per queue would be pure noise on the dashboard.
		if (!event.processed) return;
		trackMeasurement(
			TelemetryMeasurement.CommandSync,
			{ durationMs: event.durationMs, commandCount: 1 },
			{ storeName: storeNameOf(event.queue) },
		);
		return;
	}

	const base = {
		storeName: storeNameOf(event.queue),
		commandType: event.commandType,
		commandId: event.commandId,
		orderId: event.orderId,
	};

	switch (event.type) {
		case 'command:enqueued':
			trackEvent(TelemetryEvent.OfflineCommandEnqueued, base);
			break;
		case 'command:syncing':
			// Deliberately not reported: it pairs one-to-one with the outcome that
			// follows, so it would double the event volume for no extra signal.
			break;
		case 'command:succeeded':
			trackEvent(TelemetryEvent.OfflineCommandSynced, {
				...base,
				attempts: event.attempts,
				queuedForMs: event.queuedForMs,
			});
			break;
		case 'command:requeued':
			// Not an outcome yet — the command is still in flight. Tracked so a
			// command type that only ever succeeds on the third attempt is visible.
			trackEvent(TelemetryEvent.OfflineCommandEnqueued, {
				...base,
				attempts: event.attempts,
				requeued: true,
				lastError: event.lastError,
			});
			break;
		case 'command:stalled':
			// An upload that keeps failing: still queued, so not an error yet — but
			// the technician's photos are not reaching the backend.
			trackEvent(TelemetryEvent.OfflineCommandStalled, {
				...base,
				attempts: event.attempts,
				queuedForMs: event.queuedForMs,
				lastError: event.lastError,
			});
			break;
		case 'command:retried':
			// The technician pressed Retry or Send now: the command is back in flight.
			trackEvent(TelemetryEvent.OfflineCommandEnqueued, {
				...base,
				attempts: event.attempts,
				retried: true,
				lastError: event.lastError,
			});
			break;
		case 'command:discarded':
			// The technician dropped a failed command: that work will never reach the backend.
			trackEvent(TelemetryEvent.OfflineCommandDiscarded, {
				...base,
				reason: 'user',
				attempts: event.attempts,
				queuedForMs: event.queuedForMs,
				lastError: event.lastError,
			});
			break;
		case 'command:failed':
			trackEvent(TelemetryEvent.OfflineCommandExhausted, {
				...base,
				attempts: event.attempts,
				queuedForMs: event.queuedForMs,
				lastError: event.lastError,
			});
			// A permanently failed command is work the technician did that never
			// reached the backend, so it carries the extra device context the old
			// upload-logs dump used to attach. Everything else that dump carried —
			// build version, route, vendor, connectivity — already rides every Faro
			// event via `app`, `setView`, `setUser` and `instrumentDevice`.
			trackError(new Error(`Offline command failed permanently: ${event.commandType}`), {
				...base,
				attempts: event.attempts,
				lastError: event.lastError,
				...getDeviceContext(),
			});
			break;
		default:
			break;
	}
}
